// Voice settings, kept on this computer (the model is kept per computer too): on or off, hold to talk or
// press to talk, the key, and the microphone. Small on purpose: the board reads it at start, and nothing
// else of voice loads until it is on.

import { create } from "zustand";
import { readStoredJson, writeStoredJson } from "../lib/stored";
import { DEMO } from "../demo/mode";

/** hold: hold the key, let go to run. press: press once to start, again to run (David, 19 Oct). */
export type VoiceMode = "hold" | "press";

export interface VoicePrefs {
  on: boolean;
  mode: VoiceMode;
  /** the key, as KeyboardEvent.code: the same key on every keyboard layout's position */
  key: string;
  /** a microphone the user picked, by its device id; null for the computer's default */
  mic: string | null;
}

const SAVED = "cb:voice";
export const DEFAULT_KEY = "KeyV";
const DEFAULTS: VoicePrefs = { on: false, mode: "hold", key: DEFAULT_KEY, mic: null };

function read(): VoicePrefs {
  // the demo has no voice: nothing there can hear, so nothing turns it on (David, 4 Nov)
  if (DEMO) return DEFAULTS;
  const v = readStoredJson<Partial<VoicePrefs> | null>(SAVED, null);
  if (!v || typeof v !== "object") return DEFAULTS;
  return {
    on: v.on === true,
    mode: v.mode === "press" ? "press" : "hold",
    key: typeof v.key === "string" && canBeVoiceKey(v.key) ? v.key : DEFAULT_KEY,
    mic: typeof v.mic === "string" && v.mic ? v.mic : null,
  };
}

export const useVoicePrefs = create<VoicePrefs>(read);

export function setVoicePrefs(patch: Partial<VoicePrefs>) {
  if (DEMO) return;
  useVoicePrefs.setState(patch);
  writeStoredJson(SAVED, useVoicePrefs.getState()); // private mode: kept for this visit
}

/**
 * Keys that can be the voice key: one plain key that types nothing the board needs to keep, so letters, digits,
 * the backquote and F1 to F12. Never Esc (it cancels), Enter, Space, Tab, the arrows, Delete or a modifier.
 */
export function canBeVoiceKey(code: string): boolean {
  return /^(Key[A-Z]|Digit[0-9]|F([1-9]|1[0-2])|Backquote)$/.test(code);
}

/** "KeyV" is V, "Digit5" is 5, "Backquote" is `. */
export function keyName(code: string): string {
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code === "Backquote") return "`";
  return code;
}

/** While Settings waits for a new key, the voice key does nothing, so pressing V there never starts a recording. */
let picking = false;
export const pickingKey = () => picking;
export function setPickingKey(on: boolean) {
  picking = on;
}
